import React, { useState, useCallback, type KeyboardEvent } from 'react';
import Button from '@atlaskit/button';
import type { CustomTriggerProps, OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import { token } from '@atlaskit/tokens';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { useIntl } from '@atlassian/jira-intl';
import useApprovalService from '@atlassian/jira-issue-navigator-issue-operations-services/src/services/approval/index.tsx';
import useUploadPermissions from '@atlassian/jira-issue-navigator-issue-operations-services/src/services/media-upload-permissions/index.tsx';
import useOperationsAndActions from '@atlassian/jira-issue-navigator-issue-operations-services/src/services/operations-and-actions/index.tsx';
import useIssueRankService from '@atlassian/jira-issue-navigator-issue-operations-services/src/services/rank-issue/index.tsx';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { DropdownMenuGroupOld } from './dropdown-menu-group/index.tsx';
import messages from './messages.tsx';
import NotificationFlagGroup, { type Flags } from './notification-flag-group/index.tsx';

export type BaseProps = {
	issueId: number;
	issueKey: string;
	workType?: string | null;
	isCreateSubTaskAllowed?: boolean;
	onIssueRefetch?: (issueKey: string) => void;
	onIssueDelete?: (issueKey: string) => void;
};

type Props = BaseProps & {
	onAddFlag: (styleClass: string, isSuccessful: boolean) => void;
};

export const MeatballMenu = ({
	issueId,
	issueKey,
	workType,
	isCreateSubTaskAllowed,
	onIssueRefetch,
	onAddFlag,
	onIssueDelete,
}: Props) => {
	const [isOpen, setOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fetchData, state } = useOperationsAndActions();
	const { rankToTopQuery, rankToBottomQuery } = useIssueRankService();
	const { approveApproval, rejectApproval } = useApprovalService();
	const { uploadPermissions, fetchUploadPermissions } = useUploadPermissions();
	const { formatMessage } = useIntl();

	const handleChange = useCallback(
		(newOpenState: boolean) => {
			setOpen(newOpenState);
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'dropdownMenu',
				attributes: {
					isOpen: newOpenState,
				},
			});
			fireUIAnalytics(analyticsEvent, 'issueOperationsMeatballMenu');
		},
		[createAnalyticsEvent],
	);

	const onClick = () => {
		const wasClosed = !isOpen && state.loading === false && uploadPermissions.loading === false;
		if (wasClosed) {
			if (state.data === undefined) {
				fetchData(issueId);
			}
			if (uploadPermissions.permissions === null) {
				fetchUploadPermissions(issueId);
			}

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.JIRA.IssueNavigator.getSelectedIssueKey = () => issueKey;

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.JIRA.IssueNavigator.getSelectedIssueId = () => issueId;
		}
		handleChange(!isOpen);
	};

	const onRefetch = () => {
		onIssueRefetch && onIssueRefetch(issueKey);
		fetchData(issueId);
	};

	const onDelete = () => {
		onIssueDelete && onIssueDelete(issueKey);
	};

	const onRetry = () => {
		fetchData(issueId);
	};

	const onRankToTop = (onSuccess: () => void, onFailure: () => void) => {
		rankToTopQuery(issueId, onSuccess, onFailure);
	};

	const onRankToBottom = (onSuccess: () => void, onFailure: () => void) => {
		rankToBottomQuery(issueId, onSuccess, onFailure);
	};

	const onClose = () => setOpen(false);

	const onOpenChange = (attrs: OnOpenChangeArgs) => {
		setOpen(attrs.isOpen);
	};

	const onApprove = (onSuccess: () => void, onFailure: () => void) => {
		approveApproval(issueKey, onSuccess, onFailure);
	};

	const onReject = (onSuccess: () => void, onFailure: () => void) => {
		rejectApproval(issueKey, onSuccess, onFailure);
	};

	const onTriggerKeyDown = (event: KeyboardEvent<HTMLElement>) => {
		// Arrow Up/Down already mapped to table rows and meatball menu trigger should work only on Click
		if (event.key === 'ArrowDown') {
			event.stopPropagation();
		}
	};

	const trigger = ({ triggerRef, isSelected, ...triggerProps }: CustomTriggerProps) => {
		const iconColor = isSelected ? token('color.icon.selected') : token('color.icon.subtle');

		return (
			<Button
				{...triggerProps}
				id={issueId.toString()}
				testId="issue-navigator-issue-operations.ui.button"
				aria-expanded={isOpen}
				aria-label={formatMessage(messages.menuButtonLabel, { issueKey })}
				appearance="subtle"
				iconBefore={
					<MoreIcon
						label=""
						LEGACY_size="medium"
						color={iconColor}
						LEGACY_primaryColor={iconColor}
						spacing="spacious"
					/>
				}
				onClick={onClick}
				ref={triggerRef}
				onKeyDown={onTriggerKeyDown}
				isSelected={isSelected}
			/>
		);
	};

	return (
		<>
			<DropdownMenuGroupOld
				trigger={trigger}
				isOpen={isOpen}
				issueId={issueId}
				issueKey={issueKey}
				workType={workType}
				isCreateSubTaskAllowed={isCreateSubTaskAllowed}
				dataState={state}
				uploadPermissions={uploadPermissions}
				elementToFocusOnClose={`#${issueId}`}
				onIssueRefetch={onRefetch}
				onIssueDelete={onDelete}
				onClose={onClose}
				onOpenChange={onOpenChange}
				onAddFlag={onAddFlag}
				onRetry={onRetry}
				onRankToTop={onRankToTop}
				onRankToBottom={onRankToBottom}
				onApprove={onApprove}
				onReject={onReject}
			/>
			{/* Disable issue table shortcuts while meatball menu is opened (otherwise issue table will navigate with up/dow arrow keys) */}
			{isOpen && <ShortcutScope />}
		</>
	);
};

// TODO: Use Jira flag service instead
const MeatballMenuWithFlags = (props: BaseProps) => {
	const [flags, setFlags] = useState<Flags>([]);

	const onAddFlag = (styleClass: string, isSuccessful: boolean) =>
		setFlags([{ id: flags.length + 1, styleClass, isSuccessful }, ...flags]);

	const onDismissFlag = () => setFlags(flags.slice(1));

	return (
		<ContextualAnalyticsData attributes={{ issueId: props.issueId }}>
			<MeatballMenu {...props} onAddFlag={onAddFlag} />
			<NotificationFlagGroup flags={flags} onDismiss={onDismissFlag} issueKey={props.issueKey} />
		</ContextualAnalyticsData>
	);
};

export default MeatballMenuWithFlags;
